/**
 * Owner: Masa Bayu
 * File: text-model.ts
 * Date:Time: 2026-09-28 06:50
 * Description: Native QXW text model — CA-03 blocks · CA-05 link · CA-06 figure.
 */
import { createQXTextLinkInline, } from './text-link';
import { isQXTextNoteRefInline, plainTextFromNoteRef } from './text-note';
export const QX_TEXT_INLINE_TYPES = ['text', 'link', 'note-ref'];
/** Top-level body block types (CA-03 B4 · Phase 1F notes). */
export const QX_TEXT_BLOCK_TYPES = [
    'paragraph',
    'heading',
    'quote',
    'list',
    'code',
    'figure',
    'footnote',
    'endnote',
    'rule',
];
/** Leaf blocks with inlines[] (CA-03 B1). Owner "code-block" ≡ type `code`. */
export const QX_TEXT_LEAF_BLOCK_TYPES = [
    'paragraph',
    'heading',
    'quote',
    'code',
    'footnote',
    'endnote',
];
export const QX_TEXT_HEADING_LEVELS = [1, 2, 3, 4, 5, 6];
export function isQXTextEditableInline(inline) {
    return inline.type === 'text' || inline.type === 'link';
}
export function requireQXTextEditableInline(inline) {
    if (!isQXTextEditableInline(inline)) {
        throw new Error('note-ref is atomic and is not text-editable');
    }
    return inline;
}
/** Display/edit length — note-ref contributes 0 (identity-only). */
export function qxTextInlineTextLength(inline) {
    return isQXTextEditableInline(inline) ? inline.text.length : 0;
}
/** Rewrite blocks while preserving content-root language (CA-07). */
export function withQXTextModelBlocks(model, blocks) {
    return model.language !== undefined
        ? { blocks, language: model.language }
        : { blocks };
}
export function isQXTextBlockType(value) {
    return QX_TEXT_BLOCK_TYPES.includes(value);
}
export function isQXTextLeafBlockType(value) {
    return QX_TEXT_LEAF_BLOCK_TYPES.includes(value);
}
export function isQXTextHeadingLevel(value) {
    return QX_TEXT_HEADING_LEVELS.includes(value);
}
export function isQXTextLeafBlock(block) {
    return isQXTextLeafBlockType(block.type);
}
export function requireQXTextLeafBlock(block) {
    if (!isQXTextLeafBlock(block)) {
        throw new Error('expected leaf text block with inlines');
    }
    return block;
}
export function requireQXTextParagraphBlock(block) {
    if (block.type !== 'paragraph') {
        throw new Error('expected paragraph block');
    }
    return block;
}
export function createQXTextInline(text = '', marks = [], typography) {
    const inline = {
        type: 'text',
        text,
        marks: Object.freeze([...marks]),
    };
    if (typography !== undefined) {
        inline.typography = Object.freeze({ ...typography });
    }
    return inline;
}
export function isQXTextTextInline(inline) {
    return inline.type === 'text';
}
export function isQXTextLinkInlineType(inline) {
    return inline.type === 'link';
}
export function isQXTextNoteRefInlineType(inline) {
    return isQXTextNoteRefInline(inline);
}
/**
 * Rewrite display text (and optional marks) while preserving link identity.
 * Used by insert/delete/replace so mid-link edits do not demote to plain text.
 * note-ref is atomic — text rewrite is rejected (fail-closed).
 */
export function rewriteQXTextInlineText(inline, text, marks = inline.type === 'note-ref' ? [] : inline.marks) {
    if (inline.type === 'note-ref') {
        throw new Error('note-ref is atomic and has no editable text');
    }
    if (inline.type === 'link') {
        if (inline.title === undefined) {
            return createQXTextLinkInline({
                kind: inline.kind,
                target: inline.target,
                text,
                marks,
                ...(inline.typography !== undefined ? { typography: inline.typography } : {}),
            });
        }
        return createQXTextLinkInline({
            kind: inline.kind,
            target: inline.target,
            title: inline.title,
            text,
            marks,
            ...(inline.typography !== undefined ? { typography: inline.typography } : {}),
        });
    }
    return createQXTextInline(text, marks, inline.typography);
}
/** Replace marks only — preserves link kind/target/title. note-ref has no marks. */
export function withQXTextInlineMarks(inline, marks) {
    if (inline.type === 'note-ref') {
        throw new Error('note-ref is atomic and has no marks');
    }
    return rewriteQXTextInlineText(inline, inline.text, marks);
}
/** Deep-freeze an inline for command payloads (deterministic link/note-ref shape). */
export function freezeQXTextInline(inline) {
    if (inline.type === 'note-ref') {
        return Object.freeze({
            type: 'note-ref',
            targetRef: inline.targetRef,
        });
    }
    const marks = Object.freeze(inline.marks.map((mark) => Object.freeze({ ...mark })));
    const typography = inline.typography === undefined
        ? undefined
        : Object.freeze({ ...inline.typography });
    if (inline.type === 'link') {
        if (inline.title === undefined) {
            return Object.freeze({
                type: 'link',
                kind: inline.kind,
                target: inline.target,
                text: inline.text,
                marks,
                ...(typography !== undefined ? { typography } : {}),
            });
        }
        return Object.freeze({
            type: 'link',
            kind: inline.kind,
            target: inline.target,
            title: inline.title,
            text: inline.text,
            marks,
            ...(typography !== undefined ? { typography } : {}),
        });
    }
    return Object.freeze({
        type: 'text',
        text: inline.text,
        marks,
        ...(typography !== undefined ? { typography } : {}),
    });
}
function copyInlines(inlines = [createQXTextInline()]) {
    return [...inlines];
}
export function createQXTextParagraphBlock(inlines = [createQXTextInline()], format) {
    const block = {
        type: 'paragraph',
        inlines: copyInlines(inlines),
    };
    if (format !== undefined) {
        block.format = { ...format };
    }
    return block;
}
export function createQXTextHeadingBlock(level, inlines = [createQXTextInline()]) {
    if (!isQXTextHeadingLevel(level)) {
        throw new Error('heading level must be an integer from 1 to 6');
    }
    return {
        type: 'heading',
        level,
        inlines: copyInlines(inlines),
    };
}
export function createQXTextQuoteBlock(inlines = [createQXTextInline()]) {
    return {
        type: 'quote',
        inlines: copyInlines(inlines),
    };
}
export function createQXTextCodeBlock(inlines = [createQXTextInline()]) {
    return {
        type: 'code',
        inlines: copyInlines(inlines),
    };
}
export function createQXTextListItemBlock(inlines = [createQXTextInline()], nestedList) {
    if (nestedList === undefined) {
        return {
            type: 'list-item',
            inlines: copyInlines(inlines),
        };
    }
    return {
        type: 'list-item',
        inlines: copyInlines(inlines),
        list: nestedList,
    };
}
export function createQXTextListBlock(items = [createQXTextListItemBlock()]) {
    if (items.length < 1) {
        throw new Error('list must contain at least one list-item');
    }
    return {
        type: 'list',
        items: [...items],
    };
}
/**
 * Transform a leaf block to another leaf type, preserving inlines.
 * Heading requires `level`. List conversions are rejected (use list factories).
 */
export function transformQXTextLeafBlock(block, nextType, options = {}) {
    if (block.type === 'footnote' ||
        block.type === 'endnote' ||
        nextType === 'footnote' ||
        nextType === 'endnote') {
        throw new Error('note blocks use note factories — not leaf transform');
    }
    const inlines = block.inlines;
    const language = block.language;
    const target = nextType;
    let next;
    switch (target) {
        case 'paragraph':
            next = createQXTextParagraphBlock(inlines);
            break;
        case 'heading': {
            const level = options.level ?? (block.type === 'heading' ? block.level : 1);
            next = createQXTextHeadingBlock(level, inlines);
            break;
        }
        case 'quote':
            next = createQXTextQuoteBlock(inlines);
            break;
        case 'code':
            next = createQXTextCodeBlock(inlines);
            break;
        default: {
            const _exhaustive = target;
            throw new Error(`unsupported leaf block type: ${String(_exhaustive)}`);
        }
    }
    if (language === undefined) {
        return next;
    }
    return { ...next, language };
}
export function createQXTextModel(text = '') {
    return {
        blocks: [createQXTextParagraphBlock([createQXTextInline(text)])],
    };
}
export function createQXTextContent(text = '') {
    return createQXTextModel(text);
}
export function plainTextFromInlines(inlines) {
    return inlines
        .map((inline) => {
        if (inline.type === 'note-ref') {
            return plainTextFromNoteRef(inline);
        }
        return inline.text;
    })
        .join('');
}
export function plainTextFromList(list) {
    return list.items
        .map((item) => {
        const head = plainTextFromInlines(item.inlines);
        if (item.list === undefined) {
            return head;
        }
        const nested = plainTextFromList(item.list);
        return nested.length === 0 ? head : `${head}\n${nested}`;
    })
        .join('\n');
}
export function createQXTextRuleBlock(color) {
    if (!/^#[0-9A-F]{6}$/.test(color)) {
        throw new Error('rule color must be canonical #RRGGBB');
    }
    return { type: 'rule', color };
}
//# sourceMappingURL=text-model.js.map